import React, { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Step } from "react-joyride";
import raceTheClockIcon from "../assets/race-the-clock-logo-colorful.png";
import { ColorblindImage } from "./ColorblindImage";
import { primaryIconNavItems } from "../constants/navigation";

import { tourStepsYourCollections } from "../pages/YourCollections/tourStepsYourCollections";
import { tourStepsNewCollection } from "../pages/NewCollection/tourStepsNewCollection";
import { tourStepsDiscoverCollections } from "../pages/DiscoverCollections/tourStepsDiscoverCollections";
import { tourStepsFullScreenDisplay } from "../pages/FullScreenDisplay/tourStepsFullScreenDisplay";
import { tourStepsCollectionSetup } from "../pages/CollectionSetup/tourStepsCollectionSetup";
import { tourStepsCollectionFinalStep } from "../pages/CollectionFinalStep/tourStepsCollectionFinalStep";
import { tourStepsNameGenerator } from "../pages/NameGenerator/tourStepsNameGenerator";
import { tourStepsResources } from "../pages/Resources/tourStepsResources";
import { tourStepsSettings } from "../pages/Settings/tourStepsSettings";
import { tourStepsMyAccount } from "../pages/MyAccount/tourStepsMyAccount";
import { tourStepsGames } from "../pages/Games/tourStepsGames";
import { tourStepsTimedChallenges } from "../pages/Games/tourStepsTimedChallenges";
import { tourStepsReports } from "../pages/Reports/tourStepsReports";
import { tourStepsBadgesAchievements } from "../pages/BadgesAchievements/tourStepsBadgesAchievements";
import { tourStepsMilestones } from "../pages/Milestones/tourStepsMilestones";
import { tourStepsMatchingGame } from "../pages/Games/tourStepsMatchingGame";
import { tourStepsMultipleWords } from "../pages/Games/tourStepsMultipleWords";
import { tourStepsTypingSimulator } from "../pages/Games/tourStepsTypingSimulator";
import { tourStepsMissingNumber } from "../pages/Games/tourStepsMissingNumber";
import { tourStepsTimedTrials } from "../pages/Games/tourStepsTimedTrials";
import { tourStepsMultipleChoice } from "../pages/Games/tourStepsMultipleChoice";
import { tourStepsQuizShow } from "../pages/Games/tourStepsQuizShow";
import { tourStepsShop } from "../pages/Shop/tourStepsShop";
import { tourStepsAbout } from "../pages/Home/tourStepsAbout";
import { tourStepsCredits } from "../pages/Credits/tourStepsCredits";
import { getDefaultVisibilityStates } from "../utils/tourHelpers";
import { useUserRole } from "../context/UserRoleContext";

interface NavbarProps {
  isPaused?: boolean;
  onPauseResume?: () => void;
  onBack?: () => void;
  hasBackButton?: boolean;
  onStartTour: () => void;
  setTourName: React.Dispatch<React.SetStateAction<string>>;
  setCurrentTourStep: React.Dispatch<React.SetStateAction<number>>;
  onOpenMobileMenu?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({
  isPaused,
  onPauseResume,
  onBack,
  hasBackButton,
  onStartTour,
  setTourName,
  setCurrentTourStep,
  onOpenMobileMenu,
}) => {
  const { userRole } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (path: string) => {
    console.log("Navigating to:", path);
    navigate(path);
  };

  const handleBack = () => {
    navigate("/your-collections");
  };

  const handleTitleClick = () => {
    navigate("/your-collections");
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleStartTour = useCallback(() => {
    const visibilityStates = getDefaultVisibilityStates();

    // Set visibility states based on the current location
    switch (location.pathname) {
      case "/your-collections":
        visibilityStates.isCollectionCardVisible = true;
        visibilityStates.isStartCollectionButtonVisible = true;
        visibilityStates.isEditCollectionButtonVisible = true;
        visibilityStates.isDeleteCollectionButtonVisible = true;
        break;
      case "/new-collection":
        visibilityStates.isCollectionNameVisible = true;
        visibilityStates.isCategorySelectVisible = true;
        visibilityStates.isStageSelectVisible = true;
        visibilityStates.isPublicCheckboxVisible = true;
        visibilityStates.isSubmitButtonVisible = true;
        break;
      case "/discover-collections":
        visibilityStates.isSearchInputVisible = true;
        visibilityStates.isSortSelectVisible = true;
        visibilityStates.isCollectionsGridVisible = true;
        visibilityStates.isPreviewButtonVisible = true;
        break;
      case "/collection-setup":
        visibilityStates.isDotCountTypeVisible = true;
        visibilityStates.isMinDotsVisible = true;
        visibilityStates.isMaxDotsVisible = true;
        visibilityStates.isTypeSelectVisible = true;
        visibilityStates.isItemCountVisible = true;
        visibilityStates.isCollectionItemCountVisible = true;
        visibilityStates.isDotColorVisible = true;
        visibilityStates.isDotShapeVisible = true;
        visibilityStates.isGenerateRandomSequenceButtonVisible = true;
        visibilityStates.isFileUploadVisible = true;
        visibilityStates.isClearButtonVisible = true;
        visibilityStates.isSubmitButtonVisible = true;
        visibilityStates.isGeneratedSequencePreviewVisible = true;
        visibilityStates.isNextButtonVisible = true;
        break;
      case "/collection-final-step":
        visibilityStates.isSaveButtonVisible = true;
        visibilityStates.isItemPreviewVisible = true;
        visibilityStates.isMathProblemVisible = true;
        visibilityStates.isDotButtonVisible = true;
        visibilityStates.isImageUploadVisible = true;
        break;
      case "/name-generator":
        visibilityStates.isNameInputVisible = true;
        visibilityStates.isAddNameButtonVisible = true;
        visibilityStates.isSpinButtonVisible = true;
        visibilityStates.isNamesListVisible = true;
        break;
      // Add cases for other routes as needed
      default:
        break;
    }

    let steps: Step[];
    let tourName: string;

    // Determine the steps based on the current location
    switch (location.pathname) {
      case "/badges-achievements":
        steps = tourStepsBadgesAchievements();
        tourName = "badgesAchievements";
        break;
      case "/milestones":
        steps = tourStepsMilestones();
        tourName = "milestones";
        break;
      case "/collection-final-step":
        steps = tourStepsCollectionFinalStep(visibilityStates);
        tourName = "collectionFinalStep";
        break;
      case "/collection-setup":
        steps = tourStepsCollectionSetup(
          visibilityStates,
          "default",
          "default",
          "fixed",
        );
        tourName = "collectionSetup";
        break;
      case "/discover-collections":
        steps = tourStepsDiscoverCollections(visibilityStates);
        tourName = "discoverCollections";
        break;
      case "/fullscreen-display":
        steps = tourStepsFullScreenDisplay();
        tourName = "fullscreenDisplay";
        break;
      case "/games":
        steps = tourStepsGames();
        tourName = "games";
        break;
      case "/my-account":
        steps = tourStepsMyAccount();
        tourName = "myAccount";
        break;
      case "/name-generator":
        steps = tourStepsNameGenerator(visibilityStates);
        tourName = "nameGenerator";
        break;
      case "/new-collection":
        steps = tourStepsNewCollection(visibilityStates);
        tourName = "newCollection";
        break;
      case "/reports":
        steps = tourStepsReports();
        tourName = "reports";
        break;
      case "/resources":
        steps = tourStepsResources();
        tourName = "resources";
        break;
      case "/settings":
        steps = tourStepsSettings();
        tourName = "settings";
        break;
      case "/games/timed-challenges":
        steps = tourStepsTimedChallenges();
        tourName = "timedChallenges";
        break;
      case "/your-collections":
        steps = tourStepsYourCollections(visibilityStates);
        tourName = "yourCollections";
        break;
      case "/games/matching-game":
        steps = tourStepsMatchingGame();
        tourName = "matchingGame";
        break;
      case "/games/multiple-words-game":
        steps = tourStepsMultipleWords();
        tourName = "multipleWords";
        break;
      case "/games/typing-simulator":
        steps = tourStepsTypingSimulator();
        tourName = "typingSimulator";
        break;
      case "/games/missing-number":
        steps = tourStepsMissingNumber();
        tourName = "missingNumber";
        break;
      case "/games/timed-trials":
        steps = tourStepsTimedTrials();
        tourName = "timedTrials";
        break;
      case "/games/multiple-choice":
        steps = tourStepsMultipleChoice();
        tourName = "multipleChoice";
        break;
      case "/games/quiz-show":
        steps = tourStepsQuizShow();
        tourName = "quizShow";
        break;
      case "/shop":
        steps = tourStepsShop();
        tourName = "shop";
        break;
      case "/about":
        steps = tourStepsAbout();
        tourName = "about";
        break;
      case "/credits":
        steps = tourStepsCredits();
        tourName = "credits";
        break;
      default:
        steps = [];
        tourName = "navbar";
        console.log(steps);
        break;
    }

    // Start the tour immediately
    onStartTour();
    setTourName(tourName);
    setCurrentTourStep(0);
  }, [location.pathname, onStartTour, setTourName, setCurrentTourStep]);

  return (
    <div className="flex h-[var(--icon-navbar-height)] w-full items-center justify-between bg-black px-3 shadow-md md:px-5 dark:bg-gray-800">
      <div className="flex min-w-0 shrink-0 items-center md:min-w-[5rem]">
        {location.pathname === "/fullscreen-display" ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="bg-custom-red hover:bg-custom-red-dark rounded border border-black px-3 py-1.5 text-sm font-bold text-white transition-colors duration-300"
              onClick={onBack || handleBack}
            >
              Back
            </button>
            {onPauseResume && (
              <button
                type="button"
                className="bg-custom-green hover:bg-custom-green-dark rounded border border-black px-3 py-1.5 text-sm font-bold text-white transition-colors duration-300"
                onClick={onPauseResume}
              >
                {isPaused ? "Resume" : "Pause"}
              </button>
            )}
          </div>
        ) : (
          hasBackButton && (
            <button
              type="button"
              className="bg-custom-red hover:bg-custom-red-dark rounded border border-black px-3 py-1.5 text-sm font-bold text-white transition-colors duration-300 md:px-3 md:py-2 md:text-base"
              onClick={onBack || handleBack}
            >
              Back
            </button>
          )
        )}
      </div>

      <div className="flex min-w-0 flex-1 justify-center md:grow">
        <ColorblindImage
          src={raceTheClockIcon}
          alt="Race the Clock"
          className="h-10 cursor-pointer transition-all duration-300 hover:scale-105 md:hidden"
          onClick={handleTitleClick}
        />
        <nav className="hidden md:flex md:items-center">
          <div className="flex items-center gap-2 md:gap-4 lg:gap-8 xl:gap-16 2xl:gap-32">
            <ColorblindImage
              src={raceTheClockIcon}
              alt="Race the Clock"
              className="h-28 cursor-pointer transition-all duration-300 hover:scale-105 md:h-36 lg:h-48"
              onClick={handleTitleClick}
            />
            {primaryIconNavItems
              .filter((item) => item.roles.includes(userRole))
              .map((item) => (
                <button
                  type="button"
                  key={item.path}
                  onClick={() => handleNavigate(item.path)}
                  className="group flex flex-col items-center justify-center px-1 py-2 text-white transition-all duration-300 hover:scale-105"
                >
                  <div className="relative flex items-center justify-center">
                    <ColorblindImage
                      src={item.iconSrc}
                      alt={item.iconAlt}
                      className="size-24 md:size-28 lg:size-40"
                    />
                  </div>
                </button>
              ))}
          </div>
        </nav>
      </div>

      <div className="flex shrink-0 items-center justify-end md:min-w-[5rem]">
        {onOpenMobileMenu && (
          <button
            type="button"
            className="flex size-11 flex-col items-center justify-center gap-1 rounded-lg text-white transition-colors hover:bg-white/10 md:hidden"
            onClick={onOpenMobileMenu}
            aria-label="Open navigation menu"
          >
            <span className="block h-0.5 w-6 rounded bg-white" />
            <span className="block h-0.5 w-6 rounded bg-white" />
            <span className="block h-0.5 w-6 rounded bg-white" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Navbar;
